import React, { useState, useEffect, useRef } from 'react';
// Make sure to create and import a corresponding CSS file for styling
import './AboutUs.css';

const useInView = (options) => {
    const ref = useRef(null);
    const [inView, setInView] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(([entry]) => {
            // If the element is intersecting, set inView to true
            if (entry.isIntersecting) {
                setInView(true);
                // Optional: Stop observing once it's visible to prevent re-triggering
                observer.unobserve(entry.target);
            }
        }, options);

        if (ref.current) {
            observer.observe(ref.current);
        }

        return () => {
            if (ref.current) {
                // Clean up the observer when the component unmounts
                observer.unobserve(ref.current);
            }
        };
    }, [ref, options]);

    return [ref, inView];
};

const AboutUs = () => {
    // Refs for animating sections when they scroll into view
    const [missionRef, missionInView] = useInView({ threshold: 0.1 });
    const [setsApartRef, setsApartInView] = useInView({ threshold: 0.1 });
    const [servicesRef, servicesInView] = useInView({ threshold: 0.1 });
    const [ctaRef, ctaInView] = useInView({ threshold: 0.1 });

    // Storing content in arrays makes the JSX cleaner and easier to manage
    const whatSetsUsApartData = [
        {
            icon: 'ri-earth-line',
            title: 'Eco-Friendly Travel',
            text: 'We prioritize eco-friendly accommodations, transportation, and activities to minimize our carbon footprint and preserve the planet\'s natural beauty.'
        },
        {
            icon: 'ri-community-line',
            title: 'Community Engagement',
            text: 'We believe in the transformative power of travel to create positive change. Our itineraries often include opportunities for community engagement, fostering cultural exchange and supporting local businesses.'
        },
        {
            icon: 'ri-heart-pulse-line',
            title: 'Destination Weddings with a Purpose',
            text: 'Planning your dream destination wedding? We specialize in crafting sustainable and breathtaking destination weddings that not only celebrate your love but also contribute to the conservation and well-being of the chosen destination.'
        },
    ];

    const servicesData = [
        {
            icon: 'ri-plane-line',
            title: 'Flights',
            text: ' Land Air Travels offers a wide range of flight options, connecting you to destinations near and far. Choose from various airlines and travel classes to find the perfect fit for your journey.'
        },
        {
            icon: 'ri-suitcase-line',
            title: 'Holidays',
            text: 'Whether you seek an adventurous escape, a relaxing beach retreat, or a cultural immersion, our holiday packages cater to every traveler\'s desires. Explore our carefully curated itineraries designed for sustainable and memorable experiences.'
        },
        {
            icon: 'ri-hotel-bed-line',
            title: 'Hotels',
            text: 'Discover eco-friendly and sustainable accommodations that align with our commitment to responsible tourism. We handpick hotels that prioritize environmental conservation and community engagement.'
        },
        {
            icon: 'ri-passport-line',
            title: 'Visa Assistance',
            text: 'Whether you\'re exploring a single country or embarking on a multi-country adventure, our visa services cover a wide range of destinations. Count on us to simplify the visa process for your chosen locations..'
        },
    ];

    // Placeholder style for the hero background. Replace with your actual image URL.
    const heroStyle = {
        backgroundImage: `url('/assets/images/AboutUs.png')`,
    };

    return (
        <div className="main-content">
            {/* Hero Section */}
            <div className="about-hero-section" style={heroStyle}>
                <div className="hero-overlay">
                    <div className="container text-center">
                        <h1 className="hero-heading">About Land Air Travels</h1>
                        <p className="hero-subheading">Redefining travel with a commitment to sustainable and eco-friendly practices.</p>
                    </div>
                </div>
            </div>

            {/* Main Page Content */}
            <div className="page-content">
                <div className="container">


                    <section ref={missionRef} className={`py-5 section-container ${missionInView ? 'is-visible' : ''}`}>
                        <div className="row justify-content-center">
                            <div className="col-lg-10 text-center">
                                <h2 className="section-title">About Land Air Travels</h2>
                                <p className="lead-text">
                                    Welcome to Land Air Travels, where we redefine the travel experience by combining the joy of exploration with a commitment to sustainable and eco-friendly practices. At Land Air Travels, we believe in providing you with not just a trip, but a journey that respects our planet and communities.

                                    Your trusted partner in sustainable and seamless travel experiences. At Land Air Travels, we understand that obtaining the right visas can be a crucial aspect of your journey. That's why we go the extra mile to ensure your travel dreams come true, providing not just flights, holidays, and hotels, but also hassle-free visa services.
                                </p>
                            </div>
                        </div>
                    </section>
                    {/* Mission Section */}
                    <section ref={missionRef} className={`py-5 section-container ${missionInView ? 'is-visible' : ''}`}>
                        <div className="row justify-content-center">
                            <div className="col-lg-10 text-center">
                                <h2 className="section-title">Our Mission</h2>
                                <p className="lead-text">
                                    At Land Air Travels, our mission is to make travel a force for good. We are dedicated to curating unforgettable experiences that not only enrich your life but also contribute to the well-being of the environment and local communities. We aim to create a sustainable travel ecosystem that promotes responsible tourism.
                                </p>
                            </div>
                        </div>
                    </section>

                    {/* What Sets Us Apart Section */}
                    <section ref={setsApartRef} className={`py-5 section-container ${setsApartInView ? 'is-visible' : ''}`}>
                        <h2 className="section-title">What Sets Us Apart</h2>
                        <div className="row g-4">
                            {whatSetsUsApartData.map((item, index) => (
                                <div className="col-lg-4 col-md-6" key={index}>
                                    <div className="feature-card h-100">
                                        <div className="feature-icon">
                                            <i className={item.icon}></i>
                                        </div>
                                        <h5 className="card-title">{item.title}</h5>
                                        <p className="card-text">{item.text}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* Our Services Section */}
                    <section ref={servicesRef} className={`py-5 section-container ${servicesInView ? 'is-visible' : ''}`}>
                        <h2 className="section-title">Our Services</h2>
                        <div className="row g-4">
                            {servicesData.map((service, index) => (
                                <div className="col-lg-3 col-md-6" key={index}>
                                    <div className="service-card h-100">
                                        <div className="service-icon">
                                            <i className={service.icon}></i>
                                        </div>
                                        <h5 className="card-title">{service.title}</h5>
                                        <p className="card-text">{service.text}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* Call to Action Section */}
                    <section ref={ctaRef} className={`py-5 cta-section ${ctaInView ? 'is-visible' : ''}`}>
                        <div className="row align-items-center">
                            <div className="col-lg-8">
                                <h3 className="cta-title text-white">Join Us in Greening Your Travels</h3>
                                <p className="cta-text mb-0">
                                    Embark on a journey with Land Air Travels, where every adventure is an opportunity to make a positive impact. Whether you're a solo traveler, a couple, or a group, we invite you to explore the world responsibly with us.

At Land Air Travels, we don't just sell trips; we create sustainable travel experiences that leave a lasting imprint on your heart and the world. Travel with purpose, travel with Land Air Travels.
                                </p>
                            </div>
                            <div className="col-lg-4 text-lg-end mt-4 mt-lg-0">
                                <button className="btn btn-primary btn-lg">Travel With Purpose</button>
                            </div>
                        </div>
                    </section>

                </div>
            </div>
        </div>
    );
};

export default AboutUs;